import { pipe, Record } from '@blade/std-lib'

import type { Comment } from './comment'
import type { CommentId } from './comment-id'
import type { DelugeId } from '@root/entities/user-short'

export const ReactionLike = 'like'
export const ReactionDislike = 'dislike'
export type Reaction = typeof ReactionLike | typeof ReactionDislike
export const Reactions: Array<Reaction> = [ReactionLike, ReactionDislike]

export type CommentReactionUpdateArgs = {
  commentId: CommentId
  reaction: Reaction
  userId: DelugeId
}

/**
 * Traverse the comments tree and update the reactions for a specific comment.
 * If the comment is not found at the current level, recursively search in the replies.
 * @param thread Comment[] - The array of comments representing the thread.
 * @param args CommentReactionUpdateArgs - The arguments containing the comment ID, reaction, and user ID.
 * @returns Comment[] - The updated array of comments with the reaction changes applied.
 */
export const findThreadReaction = (
  thread: Array<Comment>,
  args: CommentReactionUpdateArgs,
): boolean => {
  const { commentId, reaction, userId } = args
  const comment = thread.find((c) => c.id === commentId)
  const commentReaction = comment?.reactions?.[reaction]?.users.includes(userId)

  if (!commentReaction) {
    return thread.some((comment) => findThreadReaction(comment.replies, args))
  }

  return true
}

/**
 * Traverse the comments tree and update the reactions for a specific comment.
 * If the comment is not found at the current level, recursively search in the replies.
 * @param thread Comment[] - The array of comments representing the thread.
 * @param args CommentReactionUpdateArgs - The arguments containing the comment ID, reaction, and user ID.
 * @returns Comment[] - The updated array of comments with the reaction changes applied.
 */
export const updateThreadReactions = (
  thread: Array<Comment>,
  args: CommentReactionUpdateArgs,
): Array<Comment> => {
  const { commentId } = args
  const comment = thread.find((c) => c.id === commentId)

  if (!comment) {
    return thread.map((comment) => {
      const updated = updateThreadReactions(comment.replies, args)

      return { ...comment, replies: updated }
    })
  }

  comment.reactions = updateCommentReactions(comment.reactions, args)

  return thread
}

/**
 * Updates the reactions for a specific comment.
 * If the user has already reacted, the reaction is removed; otherwise, it is added.
 * @param reactions - The current reactions of the comment, which is a record of reaction types to user lists and counts.
 * @param args - The arguments containing the reaction type and user ID.
 * @returns The updated reactions object with the changes applied.
 */
const updateCommentReactions = (
  reactions: Comment['reactions'] | undefined,
  args: CommentReactionUpdateArgs,
): Comment['reactions'] => {
  const { reaction, userId } = args
  const existing = reactions?.[reaction]

  const oppositeReactions = discardOppositeReactions(
    reactions,
    reaction,
    userId,
  )

  if (existing) {
    const isLiked = existing.users.includes(userId)

    // Decrement reactions counter if the user has already reacted
    if (isLiked) {
      const updated = pipe(
        reactions,
        Record.set(reaction, {
          users: existing.users.filter((user) => user !== userId),
          count: existing.count - 1,
        }),
      )

      // Remove the reaction entry if no users remain
      if (updated[reaction]?.count === 0) {
        delete reactions[reaction]
      }

      return updated
    }

    // Increment reactions counter if the user has not reacted yet
    return {
      ...pipe(
        reactions,
        Record.set(reaction, {
          users: [...existing.users, userId],
          count: existing.count + 1,
        }),
      ),
      ...oppositeReactions,
    }
  }

  return {
    ...pipe(
      reactions ?? {},
      Record.set(reaction, {
        users: [userId],
        count: 1,
      }),
    ),
    ...oppositeReactions,
  }
}

const discardOppositeReactions = (
  reactions: Comment['reactions'] | undefined,
  reaction: Reaction,
  userId: DelugeId,
): Comment['reactions'] => {
  // Add a new reaction entry if it does not exist
  // Remove the opposite reaction entry if it exists
  const opposite = reaction === ReactionLike ? ReactionDislike : ReactionLike
  const existingUser = reactions?.[opposite]?.users.find((id) => id === userId)
  const oppositeReactionsCount = reactions?.[opposite]?.count ?? 0
  const oppositeFiltered = {
    users: (reactions?.[opposite]?.users ?? []).filter((id) => id !== userId),
    count: existingUser ? oppositeReactionsCount - 1 : oppositeReactionsCount,
  }

  return {
    [opposite]: oppositeFiltered,
  }
}
